/**
 * useExitAnimationFallback — unstick a Radix overlay whose exit animation never
 * finishes.
 *
 * Radix keeps a closing overlay mounted until its CSS exit animation reports
 * back. <Presence> runs `mounted --ANIMATION_OUT--> unmountSuspended
 * --ANIMATION_END--> unmounted`, and ANIMATION_END is sent from exactly one
 * place: an `animationend` / `animationcancel` listener on the node
 * (@radix-ui/react-presence). There is NO timeout. So if the WebView never
 * finishes the exit animation, the node stays in the DOM at
 * data-state="closed" — indefinitely.
 *
 * That is not hypothetical: reported from the field on a Huawei handset, the
 * mobile nav drawer could not be dismissed by the × or by tapping the backdrop,
 * while tapping a nav item still navigated the page underneath. Navigation
 * proves React state had already flipped to closed — the drawer was simply
 * never unmounted, so every later tap set a state that was already `false` and
 * nothing happened. Android WebViews under aggressive vendor power management
 * (EMUI power-saving / "Power Genie") can freeze or drop compositor animations,
 * and Huawei devices without GMS take WebView updates through AppGallery, so
 * their engine is often well behind the Play Store build we test on.
 *
 * The fallback: watch data-state, and if the node is STILL mounted a beat after
 * the exit animation should have ended, synthesise the `animationend` Radix is
 * waiting for. Devices that animate normally fire the real event first and
 * cancel the timer, so this is inert for them.
 *
 * Usage — compose it with the forwarded ref inside a Radix content/overlay
 * wrapper, and hand the result to the primitive so we observe the very node
 * Presence listens on:
 *
 *   const contentRef = useExitAnimationFallback(ref);
 *   return <DialogPrimitive.Content ref={contentRef} … />;
 */
import * as React from "react";

/** Comfortably past every exit duration we use (the longest is 200ms). */
const DEFAULT_DELAY_MS = 500;

function assignRef<T>(ref: React.Ref<T> | undefined, value: T | null) {
  if (typeof ref === "function") ref(value);
  else if (ref) (ref as { current: T | null }).current = value;
}

function makeAnimationEndEvent(animationName: string): Event {
  if (typeof AnimationEvent === "function") {
    return new AnimationEvent("animationend", { animationName });
  }
  // WebViews without the constructor: Radix only reads `animationName` off the
  // event, so a plain Event carrying it is enough.
  const event = new Event("animationend");
  Object.defineProperty(event, "animationName", { value: animationName });
  return event;
}

export function useExitAnimationFallback<T extends HTMLElement>(
  forwardedRef: React.Ref<T> | undefined,
  delayMs: number = DEFAULT_DELAY_MS,
): React.RefCallback<T> {
  // State (not a ref) so attaching the node re-runs the effect below.
  const [node, setNode] = React.useState<T | null>(null);

  const ref = React.useCallback(
    (value: T | null) => {
      setNode(value);
      assignRef(forwardedRef, value);
    },
    [forwardedRef],
  );

  React.useEffect(() => {
    if (!node) return;

    let timer: number | undefined;
    const clear = () => {
      if (timer !== undefined) window.clearTimeout(timer);
      timer = undefined;
    };

    const fire = () => {
      timer = undefined;
      // Already gone, or reopened while we waited → nothing to unstick.
      if (!node.isConnected || node.getAttribute("data-state") !== "closed") return;
      // Radix matches with `includes()`, so the first name is enough.
      const animationName = window
        .getComputedStyle(node)
        .animationName.split(",")[0]
        .trim();
      // No exit animation → Presence unmounts synchronously; it can't be stuck.
      if (!animationName || animationName === "none") return;
      node.dispatchEvent(makeAnimationEndEvent(animationName));
    };

    const sync = () => {
      if (node.getAttribute("data-state") === "closed") {
        if (timer === undefined) timer = window.setTimeout(fire, delayMs);
      } else {
        clear();
      }
    };

    // A real animation event means Presence resolved on its own.
    node.addEventListener("animationend", clear);
    node.addEventListener("animationcancel", clear);
    const observer = new MutationObserver(sync);
    observer.observe(node, { attributes: true, attributeFilter: ["data-state"] });
    sync();

    return () => {
      clear();
      observer.disconnect();
      node.removeEventListener("animationend", clear);
      node.removeEventListener("animationcancel", clear);
    };
  }, [node, delayMs]);

  return ref;
}
